import { FormEvent, useEffect, useRef, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigationType,
  useSearchParams,
} from 'react-router-dom';
import { RefreshCw, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PageContainer, PageHeader } from '../components/layout/Page';
import { formatPersonName } from '../utils/formatName';
import AiReviewStatus, { useAiReviewStatuses } from '../components/review/AiReviewStatus';
import { useReviewQueue } from '../components/review/useReviewQueue';
import {
  documentPeriod,
  documentStatus,
  quickFilterParams,
  QUICK_AI_FILTERS,
  readReviewFilters,
  reviewDocumentUrl,
  statusOptions,
} from '../components/review/reviewModel';

const control =
  'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';
const secondary =
  'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:opacity-50';
const primary =
  'rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50';
const returnAnchors = new Map<string, string>();
type Option = { id: string; name: string };
function displayDate(value: string | null) {
  if (!value) return 'Sin fecha';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Sin fecha válida'
    : date.toLocaleDateString('es-MX', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

export default function DocumentReview() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const filters = readReviewFilters(params);
  const filterKey = JSON.stringify(filters);
  const [draft, setDraft] = useState(filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [revision, setRevision] = useState(0);
  const requested = Number(params.get('batches'));
  const targetBatches =
    Number.isSafeInteger(requested) && requested > 0
      ? requested
      : filters.status === 'overdue'
        ? 5
        : 1;
  const queue = useReviewQueue(filters, targetBatches, revision);
  const aiStatuses = useAiReviewStatuses(queue.rows, revision);
  const [organizations, setOrganizations] = useState<Option[]>([]);
  const [users, setUsers] = useState<Option[]>([]);
  const [definitions, setDefinitions] = useState<Option[]>([]);
  const [optionErrors, setOptionErrors] = useState<string[]>([]);
  const [stats, setStats] = useState<{
    pending_documents: number;
    overdue_documents: number;
    documents_today: number;
  } | null>(null);
  const [statsError, setStatsError] = useState(false);
  const restored = useRef('');
  useEffect(() => {
    setDraft(JSON.parse(filterKey));
  }, [filterKey]);
  useEffect(() => {
    let active = true;
    setStats(null);
    setStatsError(false);
    setOptionErrors([]);
    Promise.allSettled([
      supabase.from('organizations').select('id, name').order('name'),
      supabase
        .from('users')
        .select('id, full_name, email')
        .eq('is_active', true)
        .eq('is_deleted', false)
        .eq('role', 'normal')
        .order('full_name'),
      supabase
        .from('document_definitions')
        .select('id, name')
        .eq('is_active', true)
        .order('name'),
      supabase.rpc('get_document_stats'),
    ]).then((results) => {
      if (!active) return;
      const [orgs, people, types, counts] = results;
      const failures: string[] = [];
      if (orgs.status === 'fulfilled' && !orgs.value.error)
        setOrganizations(orgs.value.data || []);
      else failures.push('organizaciones');
      if (people.status === 'fulfilled' && !people.value.error)
        setUsers(
          (people.value.data || []).map((person) => ({
            id: person.id,
            name: formatPersonName(person.full_name) || person.email,
          })),
        );
      else failures.push('usuarios');
      if (types.status === 'fulfilled' && !types.value.error)
        setDefinitions(types.value.data || []);
      else failures.push('tipos de documento');
      if (
        counts.status === 'fulfilled' &&
        !counts.value.error &&
        counts.value.data?.[0]
      )
        setStats(counts.value.data[0]);
      else setStatsError(true);
      setOptionErrors(failures);
    });
    return () => {
      active = false;
    };
  }, [revision]);
  useEffect(() => {
    if (queue.loading || restored.current === location.key) return;
    let id: string | undefined;
    try {
      id = location.hash.startsWith('#document-')
        ? decodeURIComponent(location.hash.slice(1))
        : navigationType === 'POP'
          ? returnAnchors.get(`/review${location.search}`)
          : undefined;
    } catch {
      return;
    }
    if (!id) return;
    const element = document.getElementById(id);
    if (element) {
      element.focus();
      element.scrollIntoView({ block: 'center' });
    } else document.getElementById('review-results')?.focus();
    restored.current = location.key;
  }, [
    queue.loading,
    location.key,
    location.hash,
    location.search,
    navigationType,
  ]);
  const apply = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams({ status: draft.status });
    Object.entries(draft).forEach(([key, value]) => {
      if (value !== 'all') next.set(key, value);
    });
    setParams(next);
    setFiltersOpen(false);
  };
  const returnTo = `/review${location.search}`;
  const activeFilters = [
    filters.sort!=='oldest' ? `Orden: ${{newest:'Más recientes',expiry:'Expiración',user:'Usuario · A–Z',document:'Documento · A–Z'}[filters.sort] || filters.sort}` : '',
    filters.ai!=='all' ? `IA: ${{human_approved:'Aprobado por revisor',pass:'Recomienda aprobar',issue:'Recomienda rechazar',review:'Por resolver',queued:'En cola',running:'Analizando',manual_review:'Manual',failed:'Falló',none:'Sin análisis'}[filters.ai] || filters.ai}` : '',
    statusOptions.find((item) => item.value === filters.status)?.label || '',
    filters.type !== 'all'
      ? definitions.find((item) => item.id === filters.type)?.name ||
        'Tipo seleccionado'
      : '',
    filters.user !== 'all'
      ? users.find((item) => item.id === filters.user)?.name ||
        'Usuario seleccionado'
      : '',
    filters.org !== 'all'
      ? organizations.find((item) => item.id === filters.org)?.name ||
        'Organización seleccionada'
      : '',
  ].filter(Boolean);
  const resultText =
    filters.status === 'overdue'
      ? `${queue.rows.length} documentos expirados encontrados${queue.more ? '; quedan documentos por consultar' : ''}.`
      : `${queue.rows.length} documentos cargados${queue.total !== null ? ` de ${queue.total}` : '; total no disponible'}.`;

  return (
    <PageContainer width="wide">
        <PageHeader
          title="Revisión de documentos"
          description="Encuentra una entrega, revisa su período y continúa donde te quedaste."
          actions={
            <button
              className={`${secondary} inline-flex items-center gap-2`}
              onClick={() => setRevision((value) => value + 1)}
              disabled={queue.loading}
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Actualizar
            </button>
          }
        />
        <section
          aria-label="Resumen general"
          className="rounded-xl border border-slate-200 bg-white p-4"
        >
          <p className="mb-3 text-xs text-slate-600">
            Resumen general · No cambia con los filtros de resultados
          </p>
          {statsError ? (
            <p role="alert" className="text-sm text-red-800">
              No se pudo cargar el resumen. Usa Actualizar para reintentar.
            </p>
          ) : stats ? (
            <dl className="grid gap-4 sm:grid-cols-3">
              {[
                ['Pendientes', stats.pending_documents, 'Entregas esperando decisión del revisor.'],
                ['Expirados', stats.overdue_documents, 'Documentos cuya vigencia ya terminó.'],
                ['Subidos hoy', stats.documents_today, 'Archivos cargados durante el día.'],
              ].map(([label, value, hint]) => (
                <div key={label} className="flex items-baseline gap-3" title={String(hint)}>
                  <dt className="text-sm text-slate-600">{label}</dt>
                  <dd className="text-xl font-semibold tabular-nums">
                    {Number(value).toLocaleString('es-MX')}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p role="status" className="text-sm text-slate-500">
              Cargando resumen…
            </p>
          )}
        </section>
        <section className="rounded-xl border border-slate-200 bg-white">
          <button type="button" aria-expanded={filtersOpen} aria-controls="review-filters" onClick={()=>setFiltersOpen(v=>!v)} className="flex w-full items-center gap-2 px-5 py-4 text-left text-sm font-medium">
            <SlidersHorizontal size={16} aria-hidden="true"/> Filtros y orden
            <ChevronDown size={16} aria-hidden="true" className={`ml-auto transition-transform ${filtersOpen?'rotate-180':''}`}/>
          </button>
        <form id="review-filters" hidden={!filtersOpen}
          onSubmit={apply}
          aria-label="Filtros de documentos"
          className="border-t border-slate-200 p-5"
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <label className="min-w-0 text-sm font-medium">
              Estado
              <select
                className={control}
                value={draft.status}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    status: event.target.value as typeof draft.status,
                  })
                }
              >
                {statusOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-sm font-medium">
              Tipo de documento
              <select
                className={control}
                value={draft.type}
                onChange={(event) =>
                  setDraft({ ...draft, type: event.target.value })
                }
              >
                <option value="all">Todos los tipos</option>
                {definitions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-sm font-medium">
              Usuario
              <select
                className={control}
                value={draft.user}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    user: event.target.value,
                    org: event.target.value === 'all' ? draft.org : 'all',
                  })
                }
              >
                <option value="all">Todos los usuarios</option>
                {users.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-sm font-medium">
              Organización
              <select
                className={control}
                value={draft.org}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    org: event.target.value,
                    user: event.target.value === 'all' ? draft.user : 'all',
                  })
                }
              >
                <option value="all">Todas las organizaciones</option>
                {organizations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-0 text-sm font-medium">Estado IA
              <select className={control} value={draft.ai} onChange={event=>setDraft({...draft,ai:event.target.value})}>
                {Object.entries({all:'Todos',human_approved:'Aprobado por revisor',pass:'Recomienda aprobar',issue:'Recomienda rechazar',review:'Por resolver',queued:'En cola',running:'Analizando',manual_review:'Manual',failed:'Falló',none:'Sin análisis'}).map(([value,label])=><option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label className="min-w-0 text-sm font-medium">Ordenar por
              <select className={control} value={draft.sort} onChange={event=>setDraft({...draft,sort:event.target.value})}>
                {Object.entries({oldest:'Más antiguos',newest:'Más recientes',expiry:'Expiración',user:'Usuario · A–Z',document:'Documento · A–Z'}).map(([value,label])=><option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>
          {optionErrors.length > 0 && (
            <p role="alert" className="mt-3 text-sm text-red-800">
              No se pudieron cargar: {optionErrors.join(', ')}.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => setRevision((value) => value + 1)}
              >
                Reintentar filtros
              </button>
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button className={primary} type="submit">
              Aplicar filtros
            </button>
            <button
              className={secondary}
              type="button"
              onClick={() => {
                setDraft({
                  status: 'pending',
                  type: 'all',
                  user: 'all',
                  org: 'all',
                  ai: 'all',
                  sort: 'oldest',
                });
                setParams(new URLSearchParams());
              }}
            >
              Restablecer pendientes
            </button>
            {Object.keys(filters).some(
              (key) =>
                draft[key as keyof typeof draft] !==
                filters[key as keyof typeof filters],
            ) && (
              <span role="status" className="text-sm text-slate-600">
                Cambios sin aplicar
              </span>
            )}
          </div>
        </form>
        </section>
        <section
          aria-labelledby="review-results"
          className="overflow-hidden rounded-xl border border-slate-200 bg-white"
        >
          <div className="space-y-3 border-b border-slate-200 p-5">
            <h2
              id="review-results"
              tabIndex={-1}
              className="text-lg font-semibold"
            >
              Resultados
            </h2>
            <div
              role="group"
              aria-label="Vistas rápidas por estado de IA"
              className="flex flex-wrap gap-2"
            >
              {QUICK_AI_FILTERS.map((chip) => {
                const active = filters.ai === chip.ai;
                return (
                  <button
                    key={chip.ai}
                    type="button"
                    aria-pressed={active}
                    title={chip.hint}
                    onClick={() => setParams(quickFilterParams(params, chip.ai))}
                    className={`rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${
                      active
                        ? 'bg-blue-700 text-white ring-blue-700'
                        : 'bg-white text-slate-700 ring-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            <div
              aria-label="Filtros aplicados"
              className="flex flex-wrap gap-2"
            >
              {activeFilters.map((value, i) => (
                <span
                  key={`${value}-${i}`}
                  className="rounded-md bg-slate-100 px-2 py-1 text-sm text-slate-700"
                >
                  {value}
                </span>
              ))}
            </div>
            <p
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="text-sm text-slate-600"
            >
              {queue.loading
                ? `Cargando documentos… ${queue.rows.length} disponibles.`
                : resultText}
            </p>
            {filters.status === 'overdue' && (
              <p className="text-xs text-slate-600">
                Expirados: documentos cuya vigencia terminó (su fecha de
                expiración ya pasó). No incluye requerimientos sin archivo. La
                búsqueda puede requerir consultar más documentos.
              </p>
            )}
            {queue.updatedAt && (
              <p className="text-xs text-slate-500">
                Actualizado:{' '}
                {queue.updatedAt.toLocaleTimeString('es-MX', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
            {queue.countError && (
              <p role="status" className="text-sm text-amber-800">
                El total no está disponible. Puedes seguir consultando los
                resultados.
              </p>
            )}
            {queue.error && (
              <p role="alert" className="text-sm text-red-800">
                {queue.error}{' '}
                <button
                  className="font-medium underline"
                  onClick={() => setRevision((value) => value + 1)}
                >
                  Reintentar documentos
                </button>
              </p>
            )}
          </div>
          <div
            className="overflow-x-auto"
            role="region"
            aria-label="Tabla de documentos, desplazable horizontalmente"
            tabIndex={0}
          >
            <table className="min-w-full text-left text-sm">
              <caption className="sr-only">
                Documentos que coinciden con los filtros aplicados
              </caption>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {[
                    'Documento y período',
                    'Usuario',
                    'Organización',
                    'Estado',
                    'IA',
                    'Carga / expiración',
                    'Acción',
                  ].map((label) => (
                    <th
                      key={label}
                      scope="col"
                      className="px-5 py-3 font-medium"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {queue.rows.map((doc) => {
                  const status = documentStatus(doc);
                  const { month, year } = documentPeriod(doc);
                  const period = new Date(
                    year,
                    month - 1,
                    1,
                  ).toLocaleDateString('es-MX', {
                    month: 'short',
                    year: 'numeric',
                  });
                  const anchor = `document-${doc.document_id}`;
                  return (
                    <tr key={doc.document_id} className="hover:bg-slate-50">
                      <th
                        scope="row"
                        className="min-w-[200px] max-w-sm px-5 py-4 font-medium"
                      >
                        <span className="block">
                          {doc.document_type || doc.document_name}
                        </span>
                        <span className="mt-1 block text-xs font-normal text-slate-600">
                          {period}
                        </span>
                      </th>
                      <td className="min-w-[180px] px-5 py-4">
                        <span className="block">
                          {formatPersonName(doc.user_full_name) || doc.user_email}
                        </span>
                        {doc.user_full_name && (
                          <span className="mt-1 block text-xs text-slate-500">
                            {doc.user_email}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {doc.organization_name || 'Sin organización'}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-md px-2 py-1 text-xs font-medium ${status.color}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-5 py-4"><AiReviewStatus status={aiStatuses[doc.document_id]}/></td>
                      <td className="min-w-[170px] px-5 py-4 text-xs text-slate-600">
                        <span className="block">
                          Carga: {displayDate(doc.upload_date)}
                        </span>
                        <span className="mt-1 block">
                          Expira: {displayDate(doc.expiry_date)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <Link
                          id={anchor}
                          onClick={() => {
                            if (returnAnchors.size > 50) returnAnchors.clear();
                            returnAnchors.set(returnTo, anchor);
                          }}
                          to={reviewDocumentUrl(
                            doc,
                            `${returnTo}#${encodeURIComponent(anchor)}`,
                          )}
                          className="inline-block rounded-lg px-2 py-2 font-medium text-blue-700 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
                          aria-label={`Revisar ${doc.document_type || doc.document_name} de ${formatPersonName(doc.user_full_name) || doc.user_email}, ${period}`}
                        >
                          Revisar
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!queue.loading && !queue.error && queue.rows.length === 0 && (
            <p className="p-6 text-sm text-slate-600">
              {queue.more && filters.status === 'overdue'
                ? 'Aún no se encontraron expirados entre los documentos consultados. Continúa la búsqueda.'
                : 'No se encontraron documentos con estos filtros. Prueba cambiarlos.'}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-5">
            {queue.more ? (
              <button
                disabled={queue.loading}
                className={secondary}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.set(
                    'batches',
                    String(
                      Math.max(targetBatches, queue.scannedBatches) +
                        (filters.status === 'overdue' ? 5 : 1),
                    ),
                  );
                  setParams(next, { replace: true });
                }}
              >
                {queue.loading
                  ? 'Cargando…'
                  : filters.status === 'overdue'
                    ? 'Buscar más expirados'
                    : 'Cargar más documentos'}
              </button>
            ) : (
              !queue.error && (
                <p className="text-sm text-slate-600">
                  Fin de los resultados disponibles.
                </p>
              )
            )}
            <a
              href="#review-results"
              className="text-sm font-medium text-blue-700 underline"
            >
              Volver al inicio de resultados
            </a>
          </div>
        </section>
    </PageContainer>
  );
}
